import { useState, type ReactElement, type SubmitEvent } from "react";

import { unavailableMessage, type FieldErrors } from "../../api/client.js";
import { FormSummary, TextField } from "../../forms/fields.js";
import { focusField, formValueOf, useFieldCheck } from "../../forms/use-field-check.js";
import { estimateRules } from "../../forms/view-model-rules.js";
import { InvoiceTable } from "../invoices/invoice-page.js";

import { postEstimate } from "./estimate-api.js";
import type { Estimate, EstimatePreview } from "./estimate-sheet.js";

/** 宛名の長さ。請求書の宛名（契約の企業名/組織名）と同じ 128 文字まで。 */
const addresseeLength = 128;

const addresseeField = "Addressee";

const noErrors: FieldErrors = {};

/**
 * 送る中身。<b>見積もる内容は画面のクエリのまま</b>（<c>tenants/plan-query.ts</c> の形。確かめるのはサーバ）に、宛名を足す。
 * クエリに宛名が書き足されていても、欄の値で置き換える。
 */
const bodyOf = (query: string, form: HTMLFormElement): URLSearchParams => {
    const body = new URLSearchParams(query);
    body.delete(addresseeField);
    body.set(addresseeField, formValueOf(form)(addresseeField));
    return body;
};

/**
 * 作成前の見積書（選んだ内容・月額・明細と、宛名の欄と「作成」）。
 *
 * <b>作成の前に確認のダイアログは出さない</b>（課金ではない。docs/01 §7）。送っている間はボタンを押せなくする。
 * 宛名は送る前にその場で見て（<c>estimateRules</c>。API の写し）、空なら送らない。
 * 送った後の断りは、宛名の分を欄の下に、ほか（申し込めない内容・叩きすぎ）を先頭の枠に出す。
 */
export function EstimateForm({ query, preview, onCreated }: {
    readonly query: string;
    readonly preview: EstimatePreview;
    /** 作った見積書と、作ったときにログインしていたか。 */
    readonly onCreated: (estimate: Estimate, signedIn: boolean) => void;
}): ReactElement {
    const check = useFieldCheck(estimateRules);
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<FieldErrors>(noErrors);

    const submit = async (form: HTMLFormElement): Promise<void> => {
        setBusy(true);
        setErrors(noErrors);
        try {
            const outcome = await postEstimate(bodyOf(query, form));
            if (outcome.kind === "created") {
                onCreated(outcome.value, outcome.signedIn);
                return;
            }
            setErrors(outcome.errors);
        } catch {
            setErrors({ "": [unavailableMessage] });
        }
        setBusy(false);
    };

    const onSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        const form = event.currentTarget;
        const invalid = check.checkAll(formValueOf(form));
        if (invalid !== undefined) {
            // 送らない。前に送った断りは今の入力のものではないので消す
            setErrors(noErrors);
            focusField(form, invalid);
            return;
        }
        void submit(form);
    };

    const summary = Object.entries(errors).filter(([name]) => name !== addresseeField).flatMap(([, messages]) => messages);
    const addresseeError = check.error(addresseeField) ?? errors[addresseeField]?.join(" ");

    return (
        <div className="panel">
            <h1 className="page-title">見積書の作成</h1>
            <p className="page-lead">選んだ内容で見積書を作ります。宛名を入れて「作成」を押すと、見積番号の付いた見積書が出ます。</p>

            <div className="mb-6">
                <div className="text-sm text-slate-500" data-estimate-summary="">{preview.summaryLabel}</div>
                <span className="mt-4 block text-sm text-slate-500">お見積金額（税込・月額）</span>
                <div className="text-2xl font-bold">{preview.totalLabel}</div>
            </div>

            <InvoiceTable invoice={preview} />

            <form
                method="post" noValidate className="print:hidden" onSubmit={onSubmit}
                onChange={(event) => { check.recheck(formValueOf(event.currentTarget)); }}
                onBlur={(event) => {
                    const name = event.target.getAttribute("name");
                    if (name !== null) {
                        check.leave(name, formValueOf(event.currentTarget));
                    }
                }}
            >
                <FormSummary messages={summary} />
                <TextField
                    label="宛名" name={addresseeField} required autoComplete="organization" maxLength={addresseeLength}
                    help="見積書の宛名（「○○ 御中」）に出ます。会社名などを入れてください。"
                    wrapperClassName="mb-6" error={addresseeError}
                />
                <button type="submit" className="btn-primary" disabled={busy}>作成</button>
            </form>
        </div>
    );
}
